import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { PageConfig, getPageConfig } from '../config/page-config/page-config';

interface PageContextType {
  pageConfig: PageConfig;
  isSidebarOpen: boolean;
  isSidebarCollapsed: boolean;
  isSidebarLocked: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebarCollapsed: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setHeaderAction: (id: string, handler: (() => void) | null) => void;
  getHeaderAction: (id: string) => (() => void) | undefined;
}

const PageContext = createContext<PageContextType | undefined>(undefined);

export const usePageContext = () => {
  const context = useContext(PageContext);
  if (!context) {
    throw new Error('usePageContext must be used within a PageProvider');
  }
  return context;
};

interface PageProviderProps {
  children: React.ReactNode;
}

export const PageProvider: React.FC<PageProviderProps> = ({ children }) => {
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [pageConfig, setPageConfig] = useState<PageConfig>(getPageConfig(location.pathname));
  const isSidebarLocked = pageConfig.sidebarMode === 'locked-collapsed';
  const [headerActions, setHeaderActions] = useState<Record<string, () => void>>({});

  // Update page metadata when the route changes.
  useEffect(() => {
    const newConfig = getPageConfig(location.pathname);
    setPageConfig(newConfig);

    // Auto-close sidebar on mobile when navigating
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
    if (newConfig.sidebarMode === 'locked-collapsed') {
      setIsSidebarCollapsed(true);
    }
  }, [location.pathname]);

  // Handle window resize to reset collapsed state on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        // Reset collapsed state on mobile/tablet
        setIsSidebarCollapsed(false);
      } else {
        // Desktop layouts always show the sidebar.
        setIsSidebarOpen(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  const setSidebarOpen = useCallback((open: boolean) => {
    setIsSidebarOpen(open);
  }, []);

  const toggleSidebarCollapsed = useCallback(() => {
    setIsSidebarCollapsed((prev) => {
      if (isSidebarLocked) return true;
      return !prev;
    });
  }, [isSidebarLocked]);

  const setSidebarCollapsed = useCallback(
    (collapsed: boolean) => {
      if (isSidebarLocked && !collapsed) return;
      setIsSidebarCollapsed(collapsed);
    },
    [isSidebarLocked]
  );

  // Header actions are registered by page components and looked up by id.
  const setHeaderAction = useCallback((id: string, handler: (() => void) | null) => {
    setHeaderActions((prev) => {
      if (!handler) {
        const { [id]: removed, ...rest } = prev;
        void removed;
        return rest;
      }
      return { ...prev, [id]: handler };
    });
  }, []);

  const getHeaderAction = useCallback(
    (id: string) => {
      return headerActions[id];
    },
    [headerActions]
  );

  // Memoize the context value to keep consumers stable across renders.
  const contextValue = useMemo(
    () => ({
      pageConfig,
      isSidebarOpen,
      isSidebarCollapsed,
      isSidebarLocked,
      toggleSidebar,
      setSidebarOpen,
      toggleSidebarCollapsed,
      setSidebarCollapsed,
      setHeaderAction,
      getHeaderAction,
    }),
    [
      pageConfig,
      isSidebarOpen,
      isSidebarCollapsed,
      isSidebarLocked,
      toggleSidebar,
      setSidebarOpen,
      toggleSidebarCollapsed,
      setSidebarCollapsed,
      setHeaderAction,
      getHeaderAction,
    ]
  );

  return <PageContext.Provider value={contextValue}>{children}</PageContext.Provider>;
};
